import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit2, UserX, UserCheck } from 'lucide-react';
import useUserStore from '@/stores/useUserStore';
import useAuthStore from '@/stores/useAuthStore';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorMessage from '@/components/ui/ErrorMessage';

const ROLE_LABELS = {
  admin: 'Administrateur',
  expert_senior: 'Expert Senior',
  expert_junior: 'Expert Junior',
  assistante: 'Assistante',
};

const ROLE_COLORS = {
  admin: 'bg-blue-100 text-blue-800',
  expert_senior: 'bg-purple-100 text-purple-800',
  expert_junior: 'bg-indigo-100 text-indigo-800',
  assistante: 'bg-slate-100 text-slate-800',
};

export default function UsersListPage() {
  const navigate = useNavigate();
  const { users, loading, error, fetchUsers, deleteUser } = useUserStore();
  const { user: currentUser } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter((user) => {
    const query = searchQuery.toLowerCase();
    const nom = (user.nom || '').toLowerCase();
    const prenom = (user.prenom || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return nom.includes(query) || prenom.includes(query) || email.includes(query);
  });

  const handleDeactivate = async (userId) => {
    if (window.confirm('Êtes-vous sûr de vouloir désactiver cet utilisateur ?')) {
      await deleteUser(userId);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Utilisateurs</h1>
            <p className="text-slate-600 mt-2">Gérez les comptes utilisateurs</p>
          </div>
          <button
            onClick={() => navigate('/admin/utilisateurs/nouveau')}
            className="flex items-center gap-2 px-4 py-2 bg-carex-600 text-white rounded-lg hover:bg-carex-700 transition"
          >
            <Plus size={20} />
            Nouvel utilisateur
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search size={20} className="absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-carex-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Loading */}
        {loading && <LoadingSpinner message="Chargement des utilisateurs..." />}

        {/* Error */}
        {error && <ErrorMessage message={error} onRetry={fetchUsers} />}

        {/* Empty */}
        {!loading && !error && filteredUsers.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
            <p className="text-slate-500 text-lg">
              {searchQuery
                ? 'Aucun utilisateur ne correspond à votre recherche.'
                : 'Aucun utilisateur enregistré.'}
            </p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && filteredUsers.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Nom</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Email</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Rôle</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Statut</th>
                  <th className="text-center px-6 py-3 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 transition">
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900">
                        {user.prenom} {user.nom}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{user.email}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${ROLE_COLORS[user.role] || 'bg-slate-100 text-slate-800'}`}
                      >
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/utilisateurs/${user.id}/modifier`)}
                          className="p-2 text-carex-600 hover:bg-carex-50 rounded-lg transition"
                          title="Modifier"
                        >
                          <Edit2 size={18} />
                        </button>
                        {user.id !== currentUser?.id && (
                          <button
                            onClick={() => handleDeactivate(user.id)}
                            className={`p-2 rounded-lg transition ${
                              user.is_active
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={user.is_active ? 'Désactiver' : 'Réactiver'}
                          >
                            {user.is_active ? <UserX size={18} /> : <UserCheck size={18} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary */}
        {!loading && !error && filteredUsers.length > 0 && (
          <div className="mt-4 text-sm text-slate-600">
            {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''} affiché
            {filteredUsers.length !== 1 ? 's' : ''}
            {searchQuery && ` (filtré par "${searchQuery}")`}
          </div>
        )}
      </div>
    </div>
  );
}
